import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calculator, AlertCircle, Wifi, Monitor, Zap, Download } from 'lucide-react';

export const BandwidthCalculator = () => {
  const [streamQuality, setStreamQuality] = useState('1080p');
  const [numStreams, setNumStreams] = useState(1);
  const [bitrate, setBitrate] = useState(0);
  const [totalBandwidth, setTotalBandwidth] = useState(0);

  const qualityBitrates = {
    '480p': 2.5,
    '720p': 5,
    '1080p': 10,
    '2k': 15,
    '4k': 25,
    '8k': 50
  };

  useEffect(() => {
    const br = qualityBitrates[streamQuality] || 5;
    setBitrate(br);
    setTotalBandwidth(br * numStreams);
  }, [streamQuality, numStreams]);

  const getRecommendation = () => {
    if (totalBandwidth <= 10) return { text: '✅ Excellent', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30' };
    if (totalBandwidth <= 25) return { text: '⚠️ Good', color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30' };
    if (totalBandwidth <= 50) return { text: '⚡ Acceptable', color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30' };
    return { text: '🔴 Upgrade Needed', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' };
  };

  const recommendation = getRecommendation();

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <div className="p-4 bg-purple-500/10 rounded-2xl border border-purple-500/20 shadow-[0_0_15px_rgba(168,85,247,0.2)]">
          <Wifi className="w-8 h-8 text-purple-400" />
        </div>
        <div>
          <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-white/60">Bandwidth Calculator</h2>
          <p className="text-muted-foreground">Estimate data usage and required internet speed.</p>
        </div>
      </div>

      <div className="glass p-8 rounded-2xl border border-white/10 space-y-8">

        {/* 1. Quality Selection */}
        <div>
          <label className="flex items-center gap-2 text-sm font-bold text-muted-foreground mb-4 uppercase tracking-wider">
            <Monitor className="w-4 h-4 text-primary" /> Stream Quality
          </label>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {Object.keys(qualityBitrates).map(quality => (
              <button
                key={quality}
                onClick={() => setStreamQuality(quality)}
                className={`px-2 py-3 rounded-xl font-bold transition-all text-sm border ${streamQuality === quality
                    ? 'bg-primary text-white border-primary shadow-[0_0_10px_rgba(var(--primary-rgb),0.3)] scale-105'
                    : 'bg-black/40 border-white/5 text-gray-400 hover:bg-white/5 hover:text-white'
                  }`}
              >
                {quality}
              </button>
            ))}
          </div>
        </div>

        {/* 2. Number of Streams */}
        <div className="p-6 bg-black/20 rounded-2xl border border-white/5">
          <div className="flex justify-between items-center mb-4">
            <label className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Simultaneous Devices</label>
            <span className="px-3 py-1 bg-primary/20 text-primary rounded-lg text-sm font-bold">{numStreams} Devices</span>
          </div>
          <input
            type="range"
            min="1"
            max="10"
            value={numStreams}
            onChange={(e) => setNumStreams(parseInt(e.target.value))}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-primary"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-2 font-mono">
            <span>1 Device</span>
            <span>10 Devices</span>
          </div>
        </div>

        {/* 3. Results Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Per Stream */}
          <div className="p-5 bg-white/5 rounded-2xl border border-white/10 text-center relative overflow-hidden group hover:border-white/20 transition-all">
            <div className="absolute inset-0 bg-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="text-4xl font-bold text-blue-400 mb-1">{bitrate}</div>
            <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Mbps per Device</div>
          </div>

          {/* Total Required */}
          <div className={`p-5 rounded-2xl border text-center relative overflow-hidden transform transition-all ${recommendation.bg} ${recommendation.border}`}>
            <div className={`text-4xl font-bold ${recommendation.color} drop-shadow-sm`}>{totalBandwidth}</div>
            <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Total Mbps Required</div>
            <div className={`mt-2 inline-block px-3 py-1 rounded-full text-xs font-bold bg-black/20 ${recommendation.color}`}>
              {recommendation.text}
            </div>
          </div>

          {/* Data Usage */}
          <div className="p-5 bg-white/5 rounded-2xl border border-white/10 text-center relative overflow-hidden group hover:border-white/20 transition-all">
            <div className="absolute inset-0 bg-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="text-4xl font-bold text-purple-400 mb-1">{(totalBandwidth * 0.45).toFixed(1)}</div>
            <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center justify-center gap-1">
              <Download className="w-3 h-3" /> GB per Hour
            </div>
          </div>
        </div>

        {/* 4. Tips */}
        <div className="flex gap-3 p-4 bg-primary/5 border border-primary/10 rounded-xl">
          <Zap className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <p className="text-sm text-gray-400 leading-relaxed">
            <strong className="text-primary">Pro Tip:</strong> We recommend having at least <strong>{(totalBandwidth * 1.3).toFixed(0)} Mbps</strong> download speed to account for network fluctuations and other household traffic.
          </p>
        </div>

      </div>
    </div>
  );
};

export default BandwidthCalculator;
